// MACHINERY V2 (Stroje) — Diely a materiál: unlink + install (M2.5).
//
// Spec: data package §5 "Actions" + ticket §7 fix 5 (parts half). Split out of
// PartsTabV2.tsx for the 300-line cap.
//
// FIX 5, PARTS HALF — THE SILENT ONE. `unlinkStockItems` has NO extraReducer
// case anywhere: customerMachineSlice.ts registers getMachine,
// getInstalledStockItems, getManufacturers, getCategories and installStockItems,
// and its builder chain ENDS at `installStockItems.rejected` (:149-152, the
// last case in the file). A
// rejected unlink therefore writes nothing to the slice, sets no error, toggles
// no loading flag and shows no toast — the row simply stays linked and the user
// is told nothing. §5 says it in as many words: "rejected NOT surfaced (no
// reducer case)". The failure is captured here, from the dispatch promise, and
// handed back so the tab can render RetryNotice with a retry that re-runs the
// SAME ids (artboard 1010, the ADDED "Chyba odpojenia" state).
//
// SUCCESS IS ALSO NEW. §5: "success toast NEVER fires here" — `customerMachine`
// declares `success` and nothing ever sets it. The toasts below go through
// `customerMachinery`'s setSuccess/setError, which is what MachineryToastHost
// (mounted once by ProfileBody) actually renders.
//
// `install` REPORTS ITS OUTCOME TO THE CALLER (M2.5 review finding R2). It used
// to swallow the rejection and resolve `void`, so PartsAddDialogV2 closed on a
// failed install and threw away the user's picked rows and quantities. It now
// returns whether the PUT landed, and the dialog closes only on true. The result
// is a BOOLEAN and not a per-item list on purpose: `installStockItems` is ONE
// PUT carrying the whole `ChangeInstalledStockItem[]` (the single dispatch
// below), so the server answers for the batch and there is no per-row outcome to
// report. The docs upload is the opposite case — a POST per file — and hands
// back the failed indices instead.
import { useCallback, useState } from "react";

import { useAppDispatch } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { installStockItems, unlinkStockItems } from "../../../customerMachineSlice";
import { setError, setSuccess } from "../../../customerMachinerySlice";
import { ChangeInstalledStockItem } from "../../../../inventory/models/models";
import useV2Confirm from "../../feedback/useV2Confirm";

export interface UnlinkFailure {
  ids: string[];
  /** Product number for a single row, or the selected count for a bulk unlink. */
  label: string;
  single: boolean;
}

export interface PartsTabActions {
  unlinkFailure: UnlinkFailure | null;
  clearUnlinkFailure: () => void;
  /** Confirms, then unlinks. `label` is only used for copy. */
  requestUnlink: (ids: string[], label: string, single: boolean) => void;
  /** Re-runs a failed unlink with no second confirmation. */
  retryUnlink: () => void;
  /** Resolves TRUE only when the whole batch was linked — the only outcome that
   *  may close the add dialog (R2). */
  install: (items: ChangeInstalledStockItem[]) => Promise<boolean>;
  busy: boolean;
}

export const usePartsTabActions = (
  machineId: string,
  reload: () => void,
  clearSelection: () => void
): PartsTabActions => {
  const dispatch = useAppDispatch();
  const confirm = useV2Confirm();
  const { translate } = useTranslation("myMachinery");
  const [unlinkFailure, setUnlinkFailure] = useState<UnlinkFailure | null>(null);
  const [busy, setBusy] = useState(false);

  const runUnlink = useCallback(
    async (failure: UnlinkFailure) => {
      setBusy(true);
      try {
        await dispatch(unlinkStockItems({ machineId, model: failure.ids })).unwrap();
        setUnlinkFailure(null);
        clearSelection();
        dispatch(setSuccess(translate("myMachinery:v2.tabs.parts.unlinked", { count: failure.ids.length })));
      } catch {
        setUnlinkFailure(failure);
      } finally {
        setBusy(false);
        reload();
      }
    },
    [clearSelection, dispatch, machineId, reload, translate]
  );

  const requestUnlink = useCallback(
    (ids: string[], label: string, single: boolean) => {
      if (!ids.length) return;
      const opened = confirm({
        title: translate("myMachinery:unlinkProductTitle"),
        text: translate("myMachinery:unlinkProductText", { name: label }),
        confirmButton: translate("myMachinery:yesUnlink"),
        closeButton: translate("common:common.no"),
        severity: "error",
        onConfirm: () => {
          runUnlink({ ids, label, single });
        },
      });
      if (!opened) dispatch(setError(translate("myMachinery:delete.confirmBusy")));
    },
    [confirm, dispatch, runUnlink, translate]
  );

  const retryUnlink = useCallback(() => {
    if (unlinkFailure) runUnlink(unlinkFailure);
  }, [runUnlink, unlinkFailure]);

  const install = useCallback(
    async (items: ChangeInstalledStockItem[]): Promise<boolean> => {
      if (!items.length) return false;
      let linked = true;
      try {
        await dispatch(installStockItems({ machineId, model: items })).unwrap();
        dispatch(setSuccess(translate("myMachinery:v2.tabs.parts.installed", { count: items.length })));
      } catch {
        linked = false;
        dispatch(setError(translate("myMachinery:v2.tabs.parts.installFailed")));
      }
      reload();
      return linked;
    },
    [dispatch, machineId, reload, translate]
  );

  return {
    unlinkFailure,
    clearUnlinkFailure: () => setUnlinkFailure(null),
    requestUnlink,
    retryUnlink,
    install,
    busy,
  };
};

export default usePartsTabActions;
